/**
 * VERSION-HISTORY (POS) — versiones por entidad DERIVADAS del registro
 * inmutable `activity` (sin tabla dedicada).
 *
 * Por qué derivado y no tabla propia: el write-path ya escribe una entrada
 * append-only por cada cambio con patch `{campo: {old, new}}`, actor, device
 * y timestamps, protegida por triggers que bloquean UPDATE/DELETE (mig 00053).
 * Cada entrada ES una versión: numerarlas y plegar los patches reconstruye
 * el estado sin doble-escritura ni segunda fuente de verdad que pueda
 * divergir. El aislamiento multi-org (CC-40) se hereda: el bucket
 * `activity_recent` solo sincroniza las orgs del usuario y el historial
 * profundo pasa por `GET /api/activity/entity/:type/:id` (membership check).
 *
 * El "sello temporal" de cada versión es un hash FNV-1a 64 ENCADENADO
 * (incluye el sello anterior): determinista y recomputable por cualquier
 * cliente desde las entradas inmutables, de modo que manipular una versión
 * intermedia cambia todos los sellos posteriores. No es un hash-chain
 * criptográfico persistido (decisión del repo: inmutabilidad por triggers).
 */

import { type ActivityPatch, type ActivityRow, changedFields, parseActivityPatch } from "./format";

/** Estado conocido de la entidad en una versión (solo campos vistos en patches). */
export type EntitySnapshot = Record<string, unknown>;

export interface EntityVersion {
  /** Nº de versión 1-based ascendente (v1 = la entrada más antigua conocida). */
  version: number;
  /** Sello temporal determinista (16 hex, encadenado con la versión anterior). */
  seal: string;
  /** Entrada de activity de la que deriva esta versión. */
  activityId: string;
  action: string | null;
  actorUserId: string | null;
  actorName: string | null;
  serverTs: string | null;
  clientTs: string | null;
  /** Qué cambió en ESTA versión (campo → {old, new}). */
  changes: ActivityPatch;
  /** Estado acumulado tras aplicar esta versión (plegado de patches). */
  snapshot: EntitySnapshot;
}

/** Milisegundos de un timestamp textual; NaN-safe (los NaN ordenan al final). */
function tsValue(ts: string | null): number {
  if (!ts) {
    return Number.POSITIVE_INFINITY;
  }
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

/**
 * Orden cronológico ASCENDENTE canónico: server_ts, desempate por id
 * (UUIDv7 ⇒ el propio id ordena por tiempo de creación).
 */
export function compareActivityAscending(
  a: Pick<ActivityRow, "server_ts" | "id">,
  b: Pick<ActivityRow, "server_ts" | "id">,
): number {
  const delta = tsValue(a.server_ts) - tsValue(b.server_ts);
  if (delta !== 0) {
    return delta;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** FNV-1a 64-bit sobre las code units del texto; hex fijo de 16 chars. */
function fnv1a64(text: string): string {
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, "0");
}

/** Forma canónica y estable del patch (campos ordenados, valores JSON). */
export function canonicalPatch(patch: ActivityPatch): string {
  return changedFields(patch)
    .map((field) => {
      const change = patch[field];
      return `${field}:${JSON.stringify(change?.old ?? null)}>${JSON.stringify(change?.new ?? null)}`;
    })
    .join(";");
}

export interface VersionSealInput {
  entityType: string;
  entityId: string;
  version: number;
  activityId: string;
  serverTs: string | null;
  changes: ActivityPatch;
  /** Sello de la versión anterior ("" para v1) — encadena la secuencia. */
  prevSeal: string;
}

/**
 * Sello temporal de una versión: hash determinista de (entidad, nº de versión,
 * instante, entrada, cambios, sello anterior). Recomputable por cualquier
 * cliente a partir del activity inmutable — alterar una versión intermedia
 * invalida todos los sellos posteriores.
 */
export function computeVersionSeal(input: VersionSealInput): string {
  const canonical = [
    input.prevSeal,
    input.entityType,
    input.entityId,
    String(input.version),
    input.serverTs ?? "",
    input.activityId,
    canonicalPatch(input.changes),
  ].join("|");
  return fnv1a64(canonical);
}

/**
 * Construye la línea de versiones de una entidad a partir de sus entradas de
 * activity (acepta cualquier orden; p. ej. el DESC de `getEntityActivityQuery`).
 * Devuelve versiones ASCENDENTES v1..vN con snapshot acumulado y sello.
 * Nunca lanza: patches malformados cuentan como versión sin cambios.
 */
export function buildEntityVersions(rows: readonly ActivityRow[]): EntityVersion[] {
  const ordered = rows.toSorted(compareActivityAscending);
  const versions: EntityVersion[] = [];
  let snapshot: EntitySnapshot = {};
  let prevSeal = "";
  for (const [index, row] of ordered.entries()) {
    const changes = parseActivityPatch(row.patch);
    snapshot = { ...snapshot };
    for (const field of changedFields(changes)) {
      snapshot[field] = changes[field]?.new;
    }
    const version = index + 1;
    const seal = computeVersionSeal({
      entityType: row.entity_type ?? "",
      entityId: row.entity_id ?? "",
      version,
      activityId: row.id,
      serverTs: row.server_ts,
      changes,
      prevSeal,
    });
    prevSeal = seal;
    versions.push({
      version,
      seal,
      activityId: row.id,
      action: row.action,
      actorUserId: row.actor_user_id,
      actorName: row.actor_name,
      serverTs: row.server_ts,
      clientTs: row.client_ts,
      changes,
      snapshot,
    });
  }
  return versions;
}

/**
 * Diff puro campo a campo entre dos versiones reconstruidas (estilo patch:
 * campo → {old, new}). Compara por igualdad estructural JSON.
 */
export function diffVersions(from: EntityVersion, to: EntityVersion): ActivityPatch {
  const fields = new Set([...Object.keys(from.snapshot), ...Object.keys(to.snapshot)]);
  const diff: ActivityPatch = {};
  for (const field of [...fields].toSorted()) {
    const oldValue = from.snapshot[field];
    const newValue = to.snapshot[field];
    if (JSON.stringify(oldValue ?? null) !== JSON.stringify(newValue ?? null)) {
      diff[field] = { old: oldValue, new: newValue };
    }
  }
  return diff;
}
